<template>
	<div
		v-if="hasContentSlot"
		ref="panel"
		:class="[
			`panel panel--${alignment} ${theme}-theme ${panelScrollClasses}`,
			{
				'is-opened': active,
			},
		]"
		:style="style"
		data-cy="panel"
		role="dialog"
		aria-relevant="additions text"
		aria-live="polite"
		aria-atomic="false"
	>
		<Trap :disabled="!showTrap || !shouldTrap">
			<slot
				v-if="hasCustomHeaderSlot"
				name="custom-header"
			/>

			<header
				v-else-if="renderHeader"
				ref="panel-header"
				class="panel__header"
				data-cy="panel-header"
			>
				<h5
					v-if="title"
					class="panel__title"
					data-cy="panel-title"
					@keyup.esc="$emit('close')"
				>
					<helio-icon
						v-if="iconToken"
						:token="iconToken"
						:weight="iconWeight"
						class="panel__icon"
					/>

					{{ title }}
				</h5>

				<div
					v-if="subtitle"
					class="panel__subtitle"
					data-cy="panel-subtitle"
				>
					{{ subtitle }}
				</div>

				<div
					@keyup.enter="$emit('close')"
					@keyup.space="$emit('close')"
					@keyup.esc="$emit('close')"
				>
					<helio-icon
						v-if="closeable"
						:token="closeIcon"
						:weight="closeIconWeight"
						class="panel__close"
						data-cy="panel-close"
						tabindex="0"
						:aria-label="$t('aria-label.close-button')"
						@click.stop.prevent="$emit('close')"
					/>
				</div>
			</header>

			<div
				ref="panel-content"
				:class="`panel__content ${contentScrollClasses}`"
				data-cy="panel-content"
				@keyup.esc="$emit('close')"
			>
				<slot name="content" />
			</div>

			<div
				v-if="hasFooterSlot"
				ref="panel-footer"
				class="panel__footer"
				data-cy="panel-footer"
			>
				<slot name="footer" />
			</div>
		</Trap>
	</div>
</template>

<script>
import Trap from 'vue-focus-lock';
import Icon from '../icon/Icon.vue';

export default {
	name: 'Panel',

	components: {
		Trap: Trap,
		'helio-icon': Icon,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
			description: 'Sets whether the Panel will be displayed',
		},

		alignment: {
			type: String,
			required: false,
			default: 'left',
			validator: value => [
				'left',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets the horizontal alignment of the Panel',
		},

		breakpoint: {
			type: Number,
			required: false,
			default: 960,
			validator: breakpoint => breakpoint > 0,
			description: 'Sets the breakpoint of the Panel',
		},

		closeIcon: {
			type: String,
			required: false,
			default: 'times',
			description: 'Sets the icon that will close the Panel',
		},

		closeIconWeight: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the close icon',
		},

		closeOnOutsideClick: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether a user can close the Panel by clicking elsewhere on the page',
		},

		closeOnOutsideClickUseCapture: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets the value of the useCapture flag in the "close on outside click" eventListener',
		},

		closeable: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the Panel can be closed by a user',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether there will be a header icon and which icon it will be',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the Panel\'s header icon',
		},

		initialType: {
			type: String,
			required: false,
			default: 'overlay',
			validator: value => [
				'push',
				'overlay',
			].indexOf(value) !== -1,
			description: 'Sets the initial type of Panel (e.g., "push", "overlay")',
		},

		shouldNeverScroll: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether scrolling should be disabled',
		},

		shouldPropagateScroll: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether body scrolling should be disabled on hover',
		},

		shouldTrap: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the user\'s focus will be trapped inside the Panel during keyboard navigation',
		},

		subtitle: {
			type: String,
			required: false,
			default: '',
			description: 'Sets the Panel\'s subtitle',
		},

		theme: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
			description: 'Sets whether the Panel\'s skin will be light or dark',
		},

		title: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Panel\'s title',
		},

		width: {
			type: Number,
			required: false,
			default: 345,
			validator: width => width > 0,
			description: 'Sets the width of the Panel',
		},
	},

	slots: {
		content: {
			required: true,
			scoped: false,
			description: 'Supplies the Panel\'s main content',
		},

		customHeader: {
			required: false,
			scoped: false,
			description: 'Supplies content to the Panel\'s header',
		},

		footer: {
			required: false,
			scoped: false,
			description: 'Supplies content to the Panel\'s footer',
		},
	},

	data() {
		return {
			viewport: 0,
			type: 'overlay',
			renderHeader: true,
			opened: false,
			showTrap: false,
			lastFocusedElement: null,
		};
	},

	computed: {
		contentScroll() {
			return (
				this.hasFooterSlot
				|| this.hasHeaderSlot
				|| this.hasCustomHeaderSlot
				|| (this.title || this.subtitle)
			);
		},

		contentScrollClasses() {
			return this.contentScroll && !this.shouldNeverScroll ? `scroll scroll--${this.theme}` : 'no-scroll';
		},

		hasContentSlot() {
			return !!this.$slots.content || !!this.$scopedSlots.content;
		},

		hasCustomHeaderSlot() {
			return !!this.$slots['custom-header'] || !!this.$scopedSlots['custom-header'];
		},

		hasFooterSlot() {
			return !!this.$slots.footer || !!this.$scopedSlots.footer;
		},

		hasHeaderSlot() {
			return !!this.$slots.header || !!this.$scopedSlots.header;
		},

		panelScrollClasses() {
			return !this.contentScroll && !this.shouldNeverScroll ? `scroll scroll--${this.theme}` : 'no-scroll';
		},

		style() {
			return this.viewport > this.breakpoint ? `width: ${this.width}px;` : '';
		},
	},

	watch: {
		active(newValue) {
			if (newValue) {
				this.animate('open');
				this.open();
			} else {
				this.animate('close');
				this.close();
			}
		},

		closeOnOutsideClick(newVal) {
			if (newVal) {
				this.enableClickOutside();
			} else {
				this.disableClickOutside();
			}
		},

		initialType(newType) {
			this.type = newType;
		},

		title() {
			this.renderHeader = false;
		},

		renderHeader() {
			if (!this.renderHeader) {
				this.$nextTick(() => {
					this.renderHeader = true;
				});
			}
		},
	},

	created() {
		if (this.initialType) {
			this.type = this.initialType;
		}
	},

	mounted() {
		// so event listeners are not added on SSR because window does not exists there
		window.addEventListener('resize', this.setViewportWidth);
		this.setViewportWidth();

		if (this.active) {
			this.animate('open');
			this.setBodyMargin(this.alignment);
			window.addEventListener('keyup', this.checkForEscape);
		}
	},

	beforeDestroy() {
		this.resetBodyMargin(this.alignment);
		window.removeEventListener('keyup', this.checkForEscape);
		window.removeEventListener('resize', this.setViewportWidth);
		this.disableClickOutside();
	},

	methods: {
		animate(action) {
			const panel = this.$el;

			if (!panel || !panel.classList) {
				return;
			}

			if (action === 'open') {
				this.enableClickOutside();
			} else {
				this.disableClickOutside();
			}
		},

		enableClickOutside() {
			if (this.closeOnOutsideClick) {
				window.addEventListener('click', this.closeOutside, this.closeOnOutsideClickUseCapture, false);
			}
		},

		disableClickOutside() {
			window.removeEventListener('click', this.closeOutside, this.closeOnOutsideClickUseCapture, false);
		},

		closeOutside(event) {
			const { target } = event;

			if (!target.closest('.panel')) {
				this.$emit('close');
			}
		},

		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27 && this.closeable) {
				this.close();
			}
		},

		setViewportWidth() {
			this.$nextTick(() => {
				this.viewport = window.innerWidth;

				if (window.innerWidth < this.breakpoint) {
					this.resetBodyMargin(this.alignment);
				}

				if (window.innerWidth > this.breakpoint && this.opened) {
					this.setBodyMargin(this.alignment);
				}
			});
		},

		open() {
			const windowWidth = window.innerWidth;
			const site = document.querySelector('html');

			if (windowWidth > this.breakpoint) {
				this.setBodyMargin(this.alignment);
			} else {
				site.classList.add('is-panel-opened');
			}

			this.lastFocusedElement = document.activeElement;
			this.showTrap = true;

			const { panel } = this.$refs;
			if (panel && !this.shouldPropagateScroll) {
				panel.addEventListener('mouseover', () => {
					document.documentElement.style.setProperty('--bodyScroll', 'hidden');
				});
				panel.addEventListener('mouseout', () => {
					document.documentElement.style.removeProperty('--bodyScroll');
				});
			}

			this.$emit('open');
			this.opened = true;
		},

		close() {
			const site = document.querySelector('html');

			site.classList.remove('is-panel-opened');
			this.resetBodyMargin(this.alignment);

			this.showTrap = false;
			setTimeout(() => {
				if (this.lastFocusedElement) {
					this.lastFocusedElement.focus();
				}
			}, 100);

			this.opened = false;
		},

		setBodyMargin(side) {
			if (this.type === 'push') {
				const { body } = document;

				if (side === 'left') {
					body.style.marginLeft = `${this.width}px`;
				} else {
					body.style.marginRight = `${this.width}px`;
				}
			}
		},

		resetBodyMargin(side) {
			const { body } = document;

			if (side === 'left') {
				body.style.marginLeft = '0';
			} else {
				body.style.marginRight = '0';
			}
		},
	},
};
</script>

<style lang="scss" src="./panel.scss" />
